import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { DroneResource } from '../models';
import { FileUploadService } from './file-upload.service';

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

interface PresignResponse {
  uploadUrl: string;
  s3Key: string;
  cdnUrl: string;
}

/** Caller-supplied metadata for a new resource row. */
export interface DroneResourceUpload {
  resourceType: string;
  caption?: string;
}

/**
 * A drone's shared documents. `adminOnly` rows are filtered server-side, and
 * operator uploads are always registered as shared — internal-only is an
 * admin-portal concept.
 */
@Injectable({
  providedIn: 'root'
})
export class DroneResourceService {
  private readonly apiUrl = environment.apiUrl;

  constructor(
    private http: HttpClient,
    private fileUploadService: FileUploadService
  ) {}

  async getResourcesByDroneId(
    droneId: string,
    resourceType?: string
  ): Promise<DroneResource[]> {
    const params = new URLSearchParams();

    if (resourceType) {
      params.append('resourceType', resourceType);
    }

    const query = params.toString();
    const url = `${this.apiUrl}/drones/${droneId}/resources${query ? `?${query}` : ''}`;

    const response = await firstValueFrom(
      this.http.get<ApiResponse<DroneResource[]>>(url)
    );

    if (!response.success || !response.data) {
      throw new Error(response.error || 'Could not load documents for this drone.');
    }

    return response.data;
  }

  /**
   * Presign, PUT to S3, then register the row. Throws on any step so callers
   * have a single error path.
   */
  async uploadResourceByDroneId(
    droneId: string,
    file: File,
    meta: DroneResourceUpload
  ): Promise<DroneResource> {
    const validationError = this.validate(file);
    if (validationError) {
      throw new Error(validationError);
    }

    const presign = await firstValueFrom(
      this.http.post<ApiResponse<PresignResponse>>(
        `${this.apiUrl}/drones/${droneId}/resources/upload-url`,
        { filename: file.name, contentType: file.type || 'application/octet-stream' }
      )
    );

    if (!presign.success || !presign.data) {
      throw new Error(presign.error || 'Could not prepare the upload.');
    }

    // The web uploadToS3 sends no x-amz-acl, which matches the Hub signing
    // drone resources private.
    await this.fileUploadService.uploadToS3(file, presign.data.uploadUrl);

    const registered = await firstValueFrom(
      this.http.post<ApiResponse<DroneResource>>(
        `${this.apiUrl}/drones/${droneId}/resources`,
        {
          resourceType: meta.resourceType,
          caption: meta.caption ?? null,
          fileName: file.name,
          fileSize: file.size,
          fileType: file.type,
          fileExtension: this.fileUploadService.getFileExtension(file.name),
          s3Key: presign.data.s3Key,
          cdnUrl: presign.data.cdnUrl,
        }
      )
    );

    if (!registered.success || !registered.data) {
      throw new Error(registered.error || 'Upload saved to storage but could not be recorded.');
    }

    return registered.data;
  }

  async deleteResource(droneId: string, resourceId: string): Promise<void> {
    const response = await firstValueFrom(
      this.http.delete<ApiResponse<never>>(
        `${this.apiUrl}/drones/${droneId}/resources/${resourceId}`
      )
    );

    if (!response.success) {
      throw new Error(response.error || 'Could not delete this document.');
    }
  }

  /** Null when acceptable, otherwise a user-facing reason. */
  validate(file: File): string | null {
    if (!this.fileUploadService.isAllowedFileType(file)) {
      return 'That file type isn\'t supported.';
    }
    if (file.size > this.fileUploadService.getMaxFileSize()) {
      return `File is too large (${this.fileUploadService.formatFileSize(file.size)}). Max is ${this.fileUploadService.formatFileSize(this.fileUploadService.getMaxFileSize())}.`;
    }
    return null;
  }

  /**
   * Resources are stored private, so a signed URL is the only way to open one.
   * It expires in an hour — fetch at click time rather than caching per row.
   */
  async getResourceDownloadUrl(
    droneId: string,
    resourceId: string
  ): Promise<string> {
    const response = await firstValueFrom(
      this.http.get<ApiResponse<{ downloadUrl: string }>>(
        `${this.apiUrl}/drones/${droneId}/resources/${resourceId}/download-url`
      )
    );

    if (!response.success || !response.data?.downloadUrl) {
      throw new Error(response.error || 'Could not open this document.');
    }

    return response.data.downloadUrl;
  }
}
